import { unitFractionMap } from "./unitFractionMap";

// Reverse of unitFractionMap (glyph → "n/d") so we can parse quantities that were
// stored with Unicode fraction characters (e.g. "1 ½") back into numbers.
const glyphToFraction: Record<string, string> = Object.fromEntries(
    Object.entries(unitFractionMap).map(([fraction, glyph]) => [glyph, fraction])
);

// Replace any Unicode fraction glyphs with their ASCII "n/d" form, inserting a
// space when a glyph follows a whole number ("1½" → "1 1/2").
function normalizeGlyphs(raw: string): string {
    return raw.replace(/(\d?)\s*([¼-¾⅐-⅞])/g, (_match, lead: string, glyph: string) => {
        const fraction = glyphToFraction[glyph];
        if (!fraction) return _match;
        return lead ? `${lead} ${fraction}` : fraction;
    });
}

/**
 * Parse a recipe quantity string into a number, or `null` when it isn't a clean
 * scalable amount. The match is strict and full-string, so anything with extra
 * text ("to taste", "1 (15-oz)") returns `null` and is left unscaled by callers.
 *
 * Handles whole ("3"), decimal ("0.5"), fraction ("1/2"), mixed ("2 1/4") and
 * the Unicode-glyph equivalents ("½", "1 ½").
 */
export function parseQuantity(raw?: string): number | null {
    if (!raw) return null;

    const normalized = normalizeGlyphs(raw).trim();
    if (!normalized) return null;

    // Mixed number: "2 1/4"
    const mixed = normalized.match(/^(\d+)\s+(\d+)\/(\d+)$/);
    if (mixed) {
        const [, whole, numerator, denominator] = mixed;
        const d = Number(denominator);
        if (d === 0) return null;
        return Number(whole) + Number(numerator) / d;
    }

    // Plain fraction: "1/2"
    const fraction = normalized.match(/^(\d+)\/(\d+)$/);
    if (fraction) {
        const [, numerator, denominator] = fraction;
        const d = Number(denominator);
        if (d === 0) return null;
        return Number(numerator) / d;
    }

    // Whole or decimal: "3", "0.5"
    const decimal = normalized.match(/^\d+(\.\d+)?$/);
    if (decimal) return Number(normalized);

    return null;
}

// Denominators common in the kitchen; covers ½ ⅓ ⅔ ¼ ¾ ⅙ ⅛ ⅜ ⅝ ⅞ etc.
const NICE_DENOMINATORS = [2, 3, 4, 6, 8];

function greatestCommonDivisor(a: number, b: number): number {
    return b === 0 ? a : greatestCommonDivisor(b, a % b);
}

/**
 * Format a scaled decimal back into a clean kitchen-fraction string (ASCII).
 * Snaps the fractional part to the nearest "nice" fraction so 0.667 → "2/3" and
 * 1.5 → "1 1/2" rather than "0.667". Callers pass the result through
 * `formatQuantity` to render the fraction as a glyph.
 */
export function formatScaledQuantity(value: number): string {
    if (!Number.isFinite(value) || value < 0) return "";

    let whole = Math.floor(value);
    const remainder = value - whole;

    // Find the closest nice fraction (including 0 and 1) to the remainder.
    let best = { numerator: 0, denominator: 1, error: remainder };
    for (const denominator of NICE_DENOMINATORS) {
        const numerator = Math.round(remainder * denominator);
        const error = Math.abs(remainder - numerator / denominator);
        if (error < best.error) best = { numerator, denominator, error };
    }

    let { numerator, denominator } = best;

    // Snapped up to a whole — carry it.
    if (numerator === denominator) {
        whole += 1;
        numerator = 0;
    }

    if (numerator === 0) {
        return String(whole);
    }

    // Reduce (e.g. 2/4 → 1/2).
    const divisor = greatestCommonDivisor(numerator, denominator);
    numerator /= divisor;
    denominator /= divisor;

    const fraction = `${numerator}/${denominator}`;
    return whole > 0 ? `${whole} ${fraction}` : fraction;
}

/**
 * Scale a quantity string by a multiplier, returning a clean ASCII result, or
 * `null` when nothing scalable could be parsed (so callers leave it unchanged).
 *
 * Handles single amounts ("2 1/4" → "1 1/8" at ×0.5) and simple ranges
 * ("2 1/2-3" → "5-6" at ×2). For a range, both sides must parse; otherwise the
 * whole string is treated as unscalable.
 */
export function scaleQuantity(raw: string | undefined, multiplier: number): string | null {
    const single = parseQuantity(raw);
    if (single !== null) return formatScaledQuantity(single * multiplier);

    // Range: "2 1/2-3", "1 – 2". Split on a hyphen/en-dash surrounded by digits.
    const parts = raw?.split(/\s*[-–]\s*/);
    if (parts?.length === 2) {
        const low = parseQuantity(parts[0]);
        const high = parseQuantity(parts[1]);
        if (low !== null && high !== null) {
            return `${formatScaledQuantity(low * multiplier)}-${formatScaledQuantity(high * multiplier)}`;
        }
    }

    return null;
}
